import type { ZodType } from "zod";

import { APP_HEADER, APP_NAMES } from "@pharmacy/domain";

import { IS_DUKAANBOOK } from "@/brand";

import { ApiError, apiErrorFrom } from "./errors";

/**
 * DukaanBook says which app it is (DukaanBook-Design.docx, section 2.1).
 * FarmashiBook sends nothing, which the server reads as FarmashiBook: so a
 * FarmashiBook web release never depends on the server knowing the header, and
 * keeps working while the server is still the version before it.
 */
const APP_HEADERS: Record<string, string> = IS_DUKAANBOOK ? { [APP_HEADER]: APP_NAMES.shop } : {};

/**
 * The only place the web version calls fetch.
 *
 * Based on the phone's apps/mobile/src/api/client.ts. It owns what no page
 * should think about: attaching the access token, refreshing it exactly once
 * when it expires, and turning every failure into an ApiError with a stable
 * code. Pages call get/post/patch and handle codes; they never see a header,
 * a 401 or a token.
 *
 * The web's differences from the phone (guide 4.8 and 5.1):
 *   - Every request says `X-Client: web`; DukaanBook's also say `X-App:
 *     DukaanBook` (built with `--mode dukaanbook`). No `X-App` is FarmashiBook.
 *   - The access token lives in this object's memory and nowhere else: not in
 *     localStorage, not in a cookie we set, not in the address. A reload loses
 *     it, and the refresh cookie gets a new one.
 *   - The refresh token is an httpOnly cookie the server sets and the browser
 *     sends by itself. This code never sees it, so a script injected into the
 *     page cannot steal it. Only the /auth/* calls send cookies.
 */

/** A slow connection must fail rather than hang. */
const TIMEOUT_MS = 15_000;

/** What a sign-in, accept-invite or refresh answers to the web: no refresh token in it. */
export interface WebTokens {
  accessToken: string;
  expiresInSeconds?: number;
}

export interface RequestOptions<T> {
  /**
   * Check the answer against the shared contract. Worth it on anything the app
   * keeps or decides on: "the server renamed a field" becomes an error here
   * instead of a blank page three clicks later.
   */
  schema?: ZodType<T>;
  /** No Authorization header and no refresh: sign-in, sign-up, codes. */
  anonymous?: boolean;
  signal?: AbortSignal;
  timeoutMs?: number;
}

export interface ApiClientOptions {
  baseUrl: string;
  /** Injected so tests can drive the client without a server. */
  fetch?: typeof fetch;
}

/** Why a session ended (an error code), for the welcome page to say. */
export type SessionLostReason = string;

/** Session endings worth saying on opening the app; an old cookie is not news. */
const TOLD_ON_OPEN = new Set(["pharmacy_deleted", "refresh_token_reused"]);

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  private accessToken: string | null = null;

  /** The branch the person is working in, sent as X-Branch-Id. */
  private branchId: string | null = null;

  /**
   * The refresh in flight, if there is one. Not an optimisation: the refresh
   * token rotates, and presenting the same one twice is how the server spots a
   * stolen token (it then ends the whole session). A page that loads three
   * things at once gets three 401s at once; all three wait on this one promise.
   */
  private refreshing: Promise<string> | null = null;

  private readonly lostListeners = new Set<(reason: SessionLostReason) => void>();
  private readonly planListeners = new Set<() => void>();

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, "");
    // Bound: calling a detached window.fetch throws "Illegal invocation".
    this.fetchImpl = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
  }

  /* ------------------------------------------------------------ the session */

  /** After a sign-in page succeeds. */
  setTokens(tokens: WebTokens): void {
    this.accessToken = tokens.accessToken;
  }

  /** Forget the access token (signing out, or the session ended). */
  forget(): void {
    this.accessToken = null;
  }

  get hasSession(): boolean {
    return this.accessToken !== null;
  }

  /**
   * Opening the app: ask for an access token with the refresh cookie.
   *
   * No cookie, or an expired one, is simply "not signed in". Only the two
   * reasons worth telling the person (the pharmacy was deleted; the session was
   * ended for security) come back as a reason for the welcome page.
   */
  async restore(): Promise<{ signedIn: true } | { signedIn: false; reason?: SessionLostReason }> {
    try {
      await this.refreshAccess({ silent: true });
      return { signedIn: true };
    } catch (e) {
      if (!(e instanceof ApiError) || e.status !== 401) throw e;
      return TOLD_ON_OPEN.has(e.code) ? { signedIn: false, reason: e.code } : { signedIn: false };
    }
  }

  /** Sign out on the server (it deletes the cookie). Best-effort: the page forgets either way. */
  async logout(): Promise<void> {
    try {
      const res = await this.send("POST", "/auth/logout", undefined, { anonymous: true });
      if (!res.ok && res.status !== 401) throw await apiErrorFrom(res);
    } finally {
      this.forget();
    }
  }

  /** Which branch requests work in from now on (see branch/provider.tsx). */
  setBranch(branchId: string | null): void {
    this.branchId = branchId;
  }

  /** Told when the server ends the session. Returns the way to stop listening. */
  onSessionLost(listener: (reason: SessionLostReason) => void): () => void {
    this.lostListeners.add(listener);
    return () => this.lostListeners.delete(listener);
  }

  /** Told whenever the server refuses something with plan_expired (Phase 14). */
  onPlanRefused(listener: () => void): () => void {
    this.planListeners.add(listener);
    return () => this.planListeners.delete(listener);
  }

  /* ------------------------------------------------------------ requests */

  get<T>(path: string, options?: RequestOptions<T>): Promise<T> {
    return this.request<T>("GET", path, undefined, options);
  }

  post<T>(path: string, body?: unknown, options?: RequestOptions<T>): Promise<T> {
    return this.request<T>("POST", path, body, options);
  }

  patch<T>(path: string, body?: unknown, options?: RequestOptions<T>): Promise<T> {
    return this.request<T>("PATCH", path, body, options);
  }

  private async request<T>(method: string, path: string, body: unknown, options: RequestOptions<T> = {}): Promise<T> {
    const sentWith = this.accessToken;
    let res = await this.send(method, path, body, options);

    /**
     * One refresh and one retry, and only for an expired access token. Not for
     * a 401 that means "wrong password": refreshing would not help, and trying
     * a sign-in again would count against the account's lock.
     */
    if (res.status === 401 && !options.anonymous) {
      const failure = await apiErrorFrom(res.clone());
      if (failure.code === "invalid_access_token" || failure.code === "unauthenticated") {
        try {
          // Another request may have refreshed while this one was away: then
          // its new token is enough, and refreshing again would only rotate.
          if (this.accessToken === sentWith || this.accessToken === null) await this.refreshAccess();
        } catch (e) {
          // refreshAccess has already ended the session if it had to. No
          // internet while refreshing is reported as that, not as "sign in again".
          throw e instanceof ApiError && e.isOffline ? e : failure;
        }
        res = await this.send(method, path, body, options);
      }
    }

    if (!res.ok) {
      const error = await this.failure(res);
      if (error.isAuthFailure && !options.anonymous) this.lose(error.code);
      throw error;
    }

    if (res.status === 204) return undefined as T;
    const payload: unknown = await res.json();
    return options.schema ? options.schema.parse(payload) : (payload as T);
  }

  private async send(method: string, path: string, body: unknown, options: RequestOptions<unknown>): Promise<Response> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      // Tells the server this is the browser: the refresh token goes into the cookie.
      "X-Client": "web",
      // Which app, for DukaanBook: the server opens only this app's kind of business.
      ...APP_HEADERS,
    };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (!options.anonymous && this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`;
    if (this.branchId) headers["X-Branch-Id"] = this.branchId;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), options.timeoutMs ?? TIMEOUT_MS);
    const onCallerAbort = () => controller.abort();
    options.signal?.addEventListener("abort", onCallerAbort);

    try {
      return await this.fetchImpl(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal,
        // The refresh cookie lives on /auth. Nothing else is sent cookies.
        credentials: path.startsWith("/auth/") ? "include" : "omit",
      });
    } catch (e) {
      if (options.signal?.aborted) throw e;
      const timedOut = e instanceof Error && e.name === "AbortError";
      throw new ApiError(
        timedOut ? "timeout" : "network",
        timedOut ? "The request timed out." : "Could not reach the server.",
        0,
      );
    } finally {
      clearTimeout(timer);
      options.signal?.removeEventListener("abort", onCallerAbort);
    }
  }

  /** A failed answer as an ApiError, and "plan ended" told to whoever listens. */
  private async failure(res: Response): Promise<ApiError> {
    const error = await apiErrorFrom(res);
    if (error.isPlanExpired) for (const listener of this.planListeners) listener();
    return error;
  }

  private refreshAccess(opts: { silent?: boolean } = {}): Promise<string> {
    if (this.refreshing) return this.refreshing;
    this.refreshing = this.performRefresh(opts).finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }

  private async performRefresh({ silent }: { silent?: boolean }): Promise<string> {
    // No body: the browser sends the cookie, and the server reads it from there.
    const res = await this.send("POST", "/auth/refresh", undefined, { anonymous: true });

    if (!res.ok) {
      const error = await this.failure(res);
      /**
       * Only a 401 ends a session. A 500 or 502 means the server is having a bad
       * minute, not that the session is bad; ending it then would sign out every
       * counter in the country at once. A network failure never gets here: send
       * throws first.
       */
      if (error.status === 401) {
        this.forget();
        if (!silent) this.lose(error.code);
      }
      throw error;
    }

    const pair = (await res.json()) as WebTokens;
    this.accessToken = pair.accessToken;
    return pair.accessToken;
  }

  private lose(reason: SessionLostReason): void {
    this.forget();
    for (const listener of this.lostListeners) listener(reason);
  }
}

/** The app's one client. */
export const api = new ApiClient({ baseUrl: import.meta.env.VITE_API_URL ?? "" });
